import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Login from './Login';
import logo from '../img/Driscoll-logo.png'; 

const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if current path matches the link
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Get active link classes
  const getLinkClasses = (path, baseClasses) => {
    const isActive = isActiveLink(path);
    return `${baseClasses} ${isActive 
      ? 'text-primary-600 font-semibold border-b-2 border-primary-600' 
      : 'text-secondary-700 hover:text-primary-600'
    }`;
  };

  // Get mobile link classes
  const getMobileLinkClasses = (path, baseClasses) => {
    const isActive = isActiveLink(path);
    return `${baseClasses} ${isActive 
      ? 'text-primary-600 bg-primary-50 font-semibold border-l-4 border-primary-600' 
      : 'text-secondary-700 hover:text-primary-600 hover:bg-primary-50'
    }`;
  };

  const toggleLogin = () => {
    setShowLogin(!showLogin);
    setIsMobileMenuOpen(false);
  };

  const handleDirectLogout = async () => {
    try {
      // Navigate to home first to avoid PrivateRoute redirect
      navigate('/', { replace: true });
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 glass backdrop-blur-lg border-b border-white/10 shadow-soft safe-area-inset-top">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-14 sm:h-16">
            {/* Logo - tap target */}
            <Link 
              to="/" 
              className="flex items-center space-x-3 group min-h-[44px] min-w-[44px] justify-start -ml-1"
              onClick={closeMobileMenu}
            >
              <div className="relative">
                <img 
                  src={logo} 
                  alt="Driscoll Logo"
                  className="h-9 sm:h-10 w-auto max-w-[180px] sm:max-w-none transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-primary-500/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className={getLinkClasses('/dashboard', 'font-medium transition-all duration-200 hover:scale-105')}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/customers" 
                    className={getLinkClasses('/customers', 'font-medium transition-all duration-200 hover:scale-105')}
                  >
                    Customers
                  </Link>
                  <button
                    onClick={handleDirectLogout}
                    className="btn-outline btn-sm"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/" 
                    className={getLinkClasses('/', 'font-medium transition-all duration-200 hover:scale-105')}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/contact" 
                    className={getLinkClasses('/contact', 'font-medium transition-all duration-200 hover:scale-105')}
                  >
                    Contact
                  </Link>
                  <button
                    onClick={toggleLogin}
                    className="btn-primary btn-sm"
                  >
                    Login
                  </button>
                </>
              )}
            </div>

            {/* Mobile menu button - 44px touch target */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-3 -mr-1 rounded-lg text-secondary-600 hover:text-primary-600 hover:bg-secondary-100 transition-all duration-200 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-current" />
              ) : (
                <Menu className="w-6 h-6 text-current" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          <div 
            className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
              isMobileMenuOpen 
                ? 'max-h-96 opacity-100 pb-4' 
                : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 rounded-xl mt-2 shadow-medium border border-secondary-100">
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    onClick={closeMobileMenu}
                    className={getMobileLinkClasses('/dashboard', 'block px-4 py-3 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center')}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/customers" 
                    onClick={closeMobileMenu}
                    className={getMobileLinkClasses('/customers', 'block px-4 py-3 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center')}
                  >
                    Customers
                  </Link>
                  <button
                    onClick={handleDirectLogout}
                    className="w-full text-left px-4 py-3 text-secondary-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/" 
                    onClick={closeMobileMenu}
                    className={getMobileLinkClasses('/', 'block px-4 py-3 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center')}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/contact" 
                    onClick={closeMobileMenu}
                    className={getMobileLinkClasses('/contact', 'block px-4 py-3 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center')}
                  >
                    Contact
                  </Link>
                  <button
                    onClick={toggleLogin}
                    className="w-full text-left px-4 py-3 text-secondary-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-all duration-200 min-h-[44px] flex items-center"
                  >
                    Login
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Login Form Slide-In */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-strong transform transition-all duration-300 ease-in-out z-50 ${
          showLogin ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <Login onClose={toggleLogin} />
      </div>

      {/* Overlay for mobile slide-ins */}
      {showLogin && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => {
            setShowLogin(false);
          }}
        />
      )}
    </>
  );
};

export default Header;
